/**
 * Formatting, in one place, because inconsistency here reads as sloppiness in a
 * product an executive is asked to trust.
 *
 * `Intl` with an explicit `en-GB` locale rather than the browser's: the platform
 * ships one written language, dates in a method's audit trail have to sort
 * visually the way they sort chronologically, and a reviewer in one office must
 * not read a different date from a reviewer in another. Numbers are grouped
 * because a node count in the thousands is otherwise a wall of digits.
 */

const LOCALE = 'en-GB';

const DATE_TIME = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const DATE_ONLY = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const TIME_ONLY = new Intl.DateTimeFormat(LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const INTEGER = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

const PERCENT = new Intl.NumberFormat(LOCALE, {
  style: 'percent',
  maximumFractionDigits: 0,
});

/** An unparseable timestamp renders as an em dash, never as `Invalid Date`. */
const EMPTY = '—';

const asDate = (value: string | number | Date | null | undefined): Date | null => {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (value: string | number | Date | null | undefined): string => {
  const date = asDate(value);
  return date === null ? EMPTY : DATE_TIME.format(date);
};

export const formatDate = (value: string | number | Date | null | undefined): string => {
  const date = asDate(value);
  return date === null ? EMPTY : DATE_ONLY.format(date);
};

export const formatTime = (value: string | number | Date | null | undefined): string => {
  const date = asDate(value);
  return date === null ? EMPTY : TIME_ONLY.format(date);
};

/** Machine-readable companion for a `<time datetime>` attribute. */
export const toIsoAttribute = (value: string | number | Date | null | undefined): string | undefined => {
  const date = asDate(value);
  return date === null ? undefined : date.toISOString();
};

export const formatInteger = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value) ? EMPTY : INTEGER.format(value);

/** Takes a fraction (0.42), not a percentage (42) — the units mistake to make once. */
export const formatPercent = (fraction: number | null | undefined): string =>
  fraction === null || fraction === undefined || !Number.isFinite(fraction)
    ? EMPTY
    : PERCENT.format(fraction);

/**
 * "3 activities" / "1 activity". Enough for a UI in one language; a real i18n
 * layer would use `Intl.PluralRules`, and this is the seam to replace when that
 * day comes.
 */
export const pluralise = (count: number, singular: string, plural?: string): string =>
  `${formatInteger(count)} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;

/**
 * Relative time, for "edited 4 minutes ago" in a presence indicator. Absolute
 * time stays available in a tooltip: relative time is friendlier to read and
 * useless as a record.
 */
const RELATIVE = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

const RELATIVE_STEPS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['second', 1000],
  ['minute', 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['year', 365 * 24 * 60 * 60 * 1000],
];

/** `now` is a parameter, not `Date.now()`, so a test can pin it. */
export const formatRelative = (
  value: string | number | Date | null | undefined,
  now: number,
): string => {
  const date = asDate(value);
  if (date === null) return EMPTY;

  const elapsed = date.getTime() - now;
  const magnitude = Math.abs(elapsed);

  let chosen: readonly [Intl.RelativeTimeFormatUnit, number] = RELATIVE_STEPS[0] as [
    Intl.RelativeTimeFormatUnit,
    number,
  ];
  for (const step of RELATIVE_STEPS) {
    if (magnitude >= step[1]) chosen = step;
  }

  return RELATIVE.format(Math.round(elapsed / chosen[1]), chosen[0]);
};

/** Byte sizes for an attachment row. Binary units, labelled as such. */
export const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return EMPTY;
  if (bytes < 1024) return `${INTEGER.format(bytes)} B`;

  const units = ['KiB', 'MiB', 'GiB', 'TiB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits: value < 10 ? 1 : 0 }).format(value)} ${units[unit] ?? 'KiB'}`;
};

export { EMPTY as EMPTY_VALUE };
